/**
 * Game glue: everything that turns game state into sound events. Mostly
 * observes existing state each tick (player movement, stats, mobs, arrows,
 * TNT, nearby blocks) and chains onto existing callbacks, so the game systems
 * themselves stay untouched; main.ts only adds a few explicit calls for
 * actions that leave no observable trace (break / place / door / chest / eat / bow).
 */
import type * as THREE from 'three';
import { BLOCKS, FIRE_ID, isFurnaceLit, LAVA_ID, WATER_ID } from '../blocks';
import { AudioManager } from './AudioManager';
import { MusicScheduler } from './music';
import { materialOf } from './materials';
import { ORB_PITCHES, armorEquipSound, type MusicMood } from './registry';
import { StepTracker } from './steps';
import { uiSound } from '../ui/kit';
import { movementVibration, wardenHooks } from '../entities/boss/wardenHooks';

export interface AudioDeps {
  camera: THREE.Camera;
  world: { getBlock(x: number, y: number, z: number): number; getSky(x: number, y: number, z: number): number; getBlockLight(x: number, y: number, z: number): number };
  player: {
    position: { x: number; y: number; z: number };
    onGround: boolean; sneaking: boolean; flying: boolean; inWater: boolean; headInWater: boolean; inLava: boolean;
    spectator: boolean;
    onFall: ((d: number) => void) | null;
    poseView(alpha?: number): { usingBow: boolean };
  };
  stats: { health: number; dead: boolean; hurtTime: number; xpLevel: number; food: number; fire: number; air: number };
  mobs: () => readonly { id: number; type: string; x: number; y: number; z: number; hurtTime: number; dying: boolean; swell: number; removed: boolean; baby?: boolean }[];
  arrows: () => readonly { id: number; x: number; y: number; z: number; stuck: boolean; removed: boolean }[];
  charges: (fn: (c: { x: number; y: number; z: number }) => void) => void;
  tnt: { onExplode: ((x: number, y: number, z: number, power: number) => void) | null };
  /** ItemEntityHost / XpHost objects; their optional onPickup gets chained. */
  itemHost: object;
  xpHost: object;
  /** Wanted music mood right now. */
  mood: () => MusicMood;
  /** Game paused (pause menu): world sounds hold, music continues like vanilla. */
  paused: () => boolean;
  /** Current dimension id ('overworld' | 'nether' | ...). */
  dimension?: () => string;
  /** Worn armor stacks (equip sounds on change) and an item-name lookup. */
  armor?: () => readonly { id: number; count: number }[];
  itemName?: (id: number) => string;
  /** HUD whose toast("Your X broke") doubles as the tool-break event. */
  hud?: { toast(text: string): void };
}

const TICK = 0.05;
const PORTAL_ID = BLOCKS.findIndex((b) => b && /nether portal/i.test(b.name));

export class GameAudio {
  readonly am = new AudioManager();
  readonly music = new MusicScheduler(this.am);
  private readonly steps = new StepTracker();
  private acc = 0;
  private lastX: number; private lastZ: number;
  private wasOnGround = true; private wasInWater = false; private fallVy = 0; private lastY: number;
  private lastHurt = 0; private wasDead = false; private lastLevel: number; private lastFood: number;
  private readonly mobState = new Map<number, { hurt: number; dying: boolean; amb: number; swell: number }>();
  private readonly arrowState = new Map<number, boolean>();
  private readonly arrowLast = new Map<number, { x: number; y: number; z: number }>();
  private charges = 0;
  private lastHit = 0;
  private bowDrawing = false;
  private inPortal = false;
  private cave = 0;
  private lastFallDist = 0;
  private dim = '';
  private netherLoop: { setLevel(v: number): void; stop(): void } | null = null;
  private netherMood = 0;
  private armorIds: number[] | null = null;
  private readonly mobDist = new Map<number, { x: number; z: number; acc: number }>();

  constructor(private readonly d: AudioDeps) {
    const p = d.player.position;
    this.lastX = p.x; this.lastZ = p.z; this.lastY = p.y;
    this.lastLevel = d.stats.xpLevel;
    this.lastFood = d.stats.food;
    this.wasDead = d.stats.dead;
    // warm the common sets; everything else renders on first use (a few ms each)
    this.am.preload(['stone', 'grass', 'gravel', 'wood', 'sand'].flatMap((m) => [`block.${m}.step`, `block.${m}.break`])
      .concat(['ui.button.click', 'entity.player.hurt', 'entity.item.pickup', 'entity.experience_orb.pickup', 'entity.generic.eat']));
    uiSound.click = () => this.am.play('ui.button.click');
    const prevExplode = d.tnt.onExplode;
    d.tnt.onExplode = (x, y, z, power) => {
      prevExplode?.(x, y, z, power);
      this.am.play('entity.generic.explode', { pos: { x, y, z }, volume: 4 });
    };
    const prevFall = d.player.onFall;
    d.player.onFall = (dist) => { prevFall?.(dist); this.lastFallDist = dist; };
    const ih = d.itemHost as { onPickup?(s: unknown, n: number): void };
    const prevItem = ih.onPickup?.bind(ih);
    ih.onPickup = (s, n) => { prevItem?.(s, n); this.am.play('entity.item.pickup'); };
    const xh = d.xpHost as { onPickup?(points: number): void };
    const prevXp = xh.onPickup?.bind(xh);
    xh.onPickup = (pts) => {
      prevXp?.(pts);
      this.am.play('entity.experience_orb.pickup', { pitch: ORB_PITCHES[Math.floor(Math.random() * ORB_PITCHES.length)] });
    };
    uiSound.play = (id) => this.am.play(id);
    const hud = d.hud;
    if (hud) {
      const prevToast = hud.toast.bind(hud);
      hud.toast = (text) => {
        prevToast(text);
        if (/^Your .* broke$/.test(text)) this.am.play(/shield/i.test(text) ? 'item.shield.break' : 'entity.item.break');
      };
    }
  }

  // ------------------------------------------------------- explicit events ---

  /** A sculk-sensed vibration from the player's own spot (warden / shrieker listen; entities/boss/wardenHooks.ts). */
  private vib(kind: Parameters<typeof wardenHooks.vibrate>[3]): void {
    const p = this.d.player.position;
    wardenHooks.vibrate(p.x, p.y + 0.5, p.z, kind);
  }

  private at(x: number, y: number, z: number): { x: number; y: number; z: number } { return { x: x + 0.5, y: y + 0.5, z: z + 0.5 }; }

  blockBreak(x: number, y: number, z: number, id: number): void {
    this.am.play(`block.${materialOf(id)}.break`, { pos: this.at(x, y, z) });
    wardenHooks.vibrate(x + 0.5, y + 0.5, z + 0.5, 'block_break'); // sculk senses (warden / shrieker)
  }
  blockPlace(x: number, y: number, z: number, id: number): void {
    this.am.play(`block.${materialOf(id)}.place`, { pos: this.at(x, y, z) });
    wardenHooks.vibrate(x + 0.5, y + 0.5, z + 0.5, 'block_place');
  }
  /** Mining tick: vanilla plays the hit sound every 4 ticks while breaking. */
  blockHit(x: number, y: number, z: number, id: number): void {
    const now = performance.now();
    if (now - this.lastHit < 200) return;
    this.lastHit = now;
    this.am.play(`block.${materialOf(id)}.hit`, { pos: this.at(x, y, z) });
  }
  door(x: number, y: number, z: number, opening: boolean): void {
    this.am.play(opening ? 'block.wooden_door.open' : 'block.wooden_door.close', { pos: this.at(x, y, z) });
  }
  chest(x: number, y: number, z: number, opening: boolean): void {
    this.am.play(opening ? 'block.chest.open' : 'block.chest.close', { pos: this.at(x, y, z) });
    if (opening) wardenHooks.vibrate(x + 0.5, y + 0.5, z + 0.5, 'container');
  }
  eat(): void { this.am.play('entity.generic.eat'); this.vib('eat'); }
  drink(): void { this.am.play('entity.generic.drink'); }
  bowShoot(power: number): void {
    this.vib('shoot');
    const p = this.d.player.position;
    this.am.play('entity.arrow.shoot', { pos: { x: p.x, y: p.y + 1.5, z: p.z }, pitch: 1 / (0.9 + 0.3 * Math.random()) + power * 0.5 - 0.3 });
  }
  fireExtinguish(x: number, y: number, z: number): void { this.am.play('block.fire.extinguish', { pos: this.at(x, y, z) }); }
  /** Melee outcome -> vanilla 1.9+ attack sound. */
  attack(hit: { damage: number; crit: boolean; sweep: boolean; knockback: number }, strong: boolean, sprinting: boolean): void {
    if (hit.damage <= 0) this.am.play('entity.player.attack.nodamage');
    else if (hit.crit) this.am.play('entity.player.attack.crit');
    else if (hit.sweep) this.am.play('entity.player.attack.sweep');
    else if (sprinting && strong) this.am.play('entity.player.attack.knockback');
    else this.am.play(strong ? 'entity.player.attack.strong' : 'entity.player.attack.weak');
  }
  shieldBlock(): void { this.am.play('item.shield.block'); }
  bucket(fill: boolean, lava: boolean, x: number, y: number, z: number): void {
    this.am.play(`item.bucket.${fill ? 'fill' : 'empty'}${lava ? '_lava' : ''}`, { pos: this.at(x, y, z) });
  }
  ignite(x: number, y: number, z: number): void { this.am.play('item.flintandsteel.use', { pos: this.at(x, y, z) }); }
  /** A redstone component's sound (world/redstone/sounds.ts picks the vanilla event). */
  redstone(s: { id: string; pitch?: number }, x: number, y: number, z: number): void { this.am.play(s.id, { pos: this.at(x, y, z), pitch: s.pitch }); }
  /** Any registry id (escape hatch for one-off events). */
  play(id: string, pos?: { x: number; y: number; z: number }): void { this.am.play(id, pos ? { pos } : {}); }

  // --------------------------------------------------------------- per frame ---

  update(dt: number): void {
    this.am.updateListener(this.d.camera);
    this.music.update(dt, this.d.mood());
    if (this.d.paused()) return;
    this.acc += dt;
    let n = 0;
    while (this.acc >= TICK && n++ < 4) { this.acc -= TICK; this.tick(); }
    if (this.acc > TICK) this.acc = 0;
  }

  private tick(): void {
    const { player, stats, world } = this.d;
    const p = player.position;
    // --- footsteps / swim / splash / landing ---
    const dx = p.x - this.lastX, dz = p.z - this.lastZ;
    const vy = p.y - this.lastY;
    this.lastX = p.x; this.lastZ = p.z; this.lastY = p.y;
    if (!player.spectator) {
      const s = this.steps.update({ dx, dz, onGround: player.onGround, sneaking: player.sneaking, flying: player.flying, inWater: player.inWater });
      const below = this.blockBelow();
      // vibrations: walking, swimming, jumping, landing - sneaking makes none
      const vk = movementVibration({ stepped: s, sneaking: player.sneaking, flying: player.flying, wasOnGround: this.wasOnGround, onGround: player.onGround, inWater: player.inWater, vy });
      if (vk) this.vib(vk);
      if (s === 'step' && below) this.am.play(`block.${materialOf(below)}.step`, { pos: { x: p.x, y: p.y, z: p.z } });
      else if (s === 'swim') this.am.play('entity.generic.swim', { volume: 0.5 });
      if (player.inWater && !this.wasInWater && this.fallVy < -0.25) this.am.play('entity.generic.splash', { pos: { x: p.x, y: p.y, z: p.z } });
      if (player.onGround && !this.wasOnGround && !player.inWater) {
        const fell = this.lastFallDist;
        if (fell > 3) this.am.play(fell > 7 ? 'entity.player.big_fall' : 'entity.player.small_fall');
        if (below && this.fallVy < -0.3) this.am.play(`block.${materialOf(below)}.fall`, { pos: { x: p.x, y: p.y, z: p.z } });
      }
    }
    if (player.onGround) this.lastFallDist = 0;
    this.wasOnGround = player.onGround; this.wasInWater = player.inWater; this.fallVy = vy;

    // --- player stats ---
    if (stats.hurtTime > this.lastHurt && !stats.dead) {
      const id = stats.fire > 0 || player.inLava ? 'entity.player.hurt_on_fire' : stats.air <= 0 && player.headInWater ? 'entity.player.hurt_drown' : 'entity.player.hurt';
      this.am.play(id);
    }
    this.lastHurt = stats.hurtTime;
    if (stats.dead && !this.wasDead) this.am.play('entity.player.death');
    this.wasDead = stats.dead;
    if (stats.xpLevel > this.lastLevel && stats.xpLevel % 5 === 0) {
      this.am.play('entity.player.levelup', { volume: Math.min(1, stats.xpLevel / 30) });
    }
    this.lastLevel = stats.xpLevel;
    if (stats.food > this.lastFood) this.am.play('entity.player.burp');
    this.lastFood = stats.food;

    // --- bow draw ---
    const drawing = player.poseView().usingBow;
    if (drawing && !this.bowDrawing) this.am.play('item.bow.draw');
    this.bowDrawing = drawing;

    this.armorTick();
    this.dimensionTick();
    this.mobTick();
    this.arrowTick();
    let charges = 0;
    let firstNew: { x: number; y: number; z: number } | null = null;
    this.d.charges((c) => { charges++; firstNew = c; });
    if (charges > this.charges && firstNew) this.am.play('entity.tnt.primed', { pos: firstNew });
    this.charges = charges;

    this.ambientTick(world);
  }

  private blockBelow(): number {
    const p = this.d.player.position;
    const w = this.d.world;
    const x = Math.floor(p.x), z = Math.floor(p.z);
    let id = w.getBlock(x, Math.floor(p.y - 0.2), z);
    if (!id) id = w.getBlock(x, Math.floor(p.y - 1.2), z);
    // carpets / snow layers sit in the player's own cell
    const own = w.getBlock(x, Math.floor(p.y), z);
    if (own && /carpet|snow$|petals|moss carpet/i.test(BLOCKS[own]?.name ?? '')) id = own;
    return id;
  }

  private mobTick(): void {
    const seen = new Set<number>();
    for (const m of this.d.mobs()) {
      if (m.removed) continue;
      seen.add(m.id);
      let s = this.mobState.get(m.id);
      if (!s) { s = { hurt: m.hurtTime, dying: m.dying, amb: -Math.floor(Math.random() * 80), swell: m.swell }; this.mobState.set(m.id, s); }
      const pos = { x: m.x, y: m.y + 1, z: m.z };
      // babies: their own vanilla voice where one exists (pig, chicken, wolf, cat, horse), else the adult's pitched up
      const say = (ev: string, at = pos): void => {
        if (m.baby && this.am.has(`entity.baby_${m.type}.${ev}`)) this.am.play(`entity.baby_${m.type}.${ev}`, { pos: at });
        else this.am.play(`entity.${m.type}.${ev}`, { pos: at, pitch: m.baby ? 1.5 : 1 });
      };
      if (m.dying && !s.dying) say('death');
      else if (m.hurtTime > s.hurt) say('hurt');
      // vanilla Mob.baseTick: chance rises each tick, reset to -80 (interval) on play
      if (!m.dying && Math.random() * 1000 < s.amb++) {
        s.amb = -80;
        if (m.type !== 'creeper') say('ambient');
      }
      if (m.type === 'creeper' && m.swell > 0 && s.swell <= 0) this.am.play('entity.creeper.primed', { pos });
      // mob footsteps: same distance accumulator as the player's
      let md = this.mobDist.get(m.id);
      if (!md) { md = { x: m.x, z: m.z, acc: 0 }; this.mobDist.set(m.id, md); }
      const h = Math.hypot(m.x - md.x, m.z - md.z);
      md.x = m.x; md.z = m.z;
      if (h < 2 && !m.dying) {
        md.acc += h * 0.6;
        if (md.acc > 1) { md.acc -= 1; say('step', { x: m.x, y: m.y, z: m.z }); }
      }
      s.hurt = m.hurtTime; s.dying = m.dying; s.swell = m.swell;
    }
    for (const id of this.mobState.keys()) if (!seen.has(id)) { this.mobState.delete(id); this.mobDist.delete(id); }
  }

  private arrowTick(): void {
    const seen = new Set<number>();
    for (const a of this.d.arrows()) {
      seen.add(a.id);
      const prev = this.arrowState.get(a.id);
      if (prev === undefined && !this.isPlayerNear(a, 2.5)) this.am.play('entity.arrow.shoot', { pos: a });
      if (a.stuck && prev === false) this.am.play('entity.arrow.hit', { pos: a });
      this.arrowState.set(a.id, a.stuck);
      this.arrowLast.set(a.id, { x: a.x, y: a.y, z: a.z });
    }
    for (const [id, stuck] of this.arrowState) {
      if (seen.has(id)) continue;
      this.arrowState.delete(id);
      // vanished mid-flight = hit an entity; vanished while stuck next to the player = picked up
      if (!stuck) this.am.play('entity.arrow.hit', { volume: 0.6 });
      else if (this.arrowLast.get(id) && this.isPlayerNear(this.arrowLast.get(id)!, 2.5)) this.am.play('entity.item.pickup');
      this.arrowLast.delete(id);
    }
  }

  private armorTick(): void {
    const { armor, itemName } = this.d;
    if (!armor) return;
    const ids = armor().map((s) => (s.count > 0 ? s.id : 0));
    if (this.armorIds) {
      for (let i = 0; i < ids.length; i++) {
        if (ids[i] && ids[i] !== this.armorIds[i]) { this.am.play(armorEquipSound(itemName?.(ids[i]) ?? '')); break; }
      }
    }
    this.armorIds = ids;
  }

  /** Dimension change: portal travel whoosh; the nether gets its ambient bed and mood sounds. */
  private dimensionTick(): void {
    const dim = this.d.dimension?.() ?? 'overworld';
    if (dim !== this.dim) {
      if (this.dim) this.am.play('block.portal.travel');
      this.dim = dim;
      this.netherLoop?.stop();
      this.netherLoop = null;
      if (dim === 'nether') { this.netherLoop = this.am.loop('ambient.nether_wastes.loop'); this.netherLoop.setLevel(1); }
    }
    if (dim === 'nether' && Math.random() < 1 / 1200 && ++this.netherMood) {
      const p = this.d.player.position, a = Math.random() * Math.PI * 2;
      this.am.play('ambient.nether_wastes.mood', { pos: { x: p.x + Math.cos(a) * 8, y: p.y + 2, z: p.z + Math.sin(a) * 8 } });
    }
  }

  get inNether(): boolean { return this.dim === 'nether'; }

  private isPlayerNear(o: { x: number; y: number; z: number }, r: number): boolean {
    const p = this.d.player.position;
    return Math.hypot(o.x - p.x, o.y - p.y - 1.5, o.z - p.z) < r;
  }

  /** Random nearby block ticks (vanilla animateTick): fire, lava, water, portals; cave mood. */
  private ambientTick(world: AudioDeps['world']): void {
    const p = this.d.player.position;
    const px = Math.floor(p.x), py = Math.floor(p.y), pz = Math.floor(p.z);
    for (let i = 0; i < 48; i++) {
      const x = px + Math.floor(Math.random() * 25) - 12;
      const y = py + Math.floor(Math.random() * 17) - 8;
      const z = pz + Math.floor(Math.random() * 25) - 12;
      const id = world.getBlock(x, y, z);
      if (id === 0) continue;
      const pos = { x: x + 0.5, y: y + 0.5, z: z + 0.5 };
      if (id === FIRE_ID) { if (Math.random() < 0.15) this.am.play('block.fire.ambient', { pos }); }
      else if (id === LAVA_ID) {
        if (world.getBlock(x, y + 1, z) === 0) {
          if (Math.random() < 0.04) this.am.play('block.lava.pop', { pos });
          else if (Math.random() < 0.02) this.am.play('block.lava.ambient', { pos });
        }
      } else if (id !== WATER_ID && /water/i.test(BLOCKS[id]?.name ?? '') && !BLOCKS[id]?.waterlogged) {
        if (Math.random() < 0.03) this.am.play('block.water.ambient', { pos }); // flowing water only, like vanilla
      } else if (id === PORTAL_ID) { if (Math.random() < 0.05) this.am.play('block.portal.ambient', { pos }); }
      else if (isFurnaceLit(id)) { if (Math.random() < 0.1) this.am.play('block.furnace.fire_crackle', { pos }); }
    }
    // stepping into a portal
    if (PORTAL_ID > 0) {
      const inP = world.getBlock(px, py, pz) === PORTAL_ID || world.getBlock(px, py + 1, pz) === PORTAL_ID;
      if (inP && !this.inPortal) this.am.play('block.portal.trigger');
      this.inPortal = inP;
    }
    // cave mood: builds while the head is dark and cut off from the sky
    const hx = px, hy = py + 1, hz = pz;
    const dark = world.getSky(hx, hy, hz) === 0 && world.getBlockLight(hx, hy, hz) < 4;
    this.cave = dark ? this.cave + 1 / 1800 : Math.max(0, this.cave - 1 / 200);
    if (this.cave >= 1) {
      this.cave = 0;
      const a = Math.random() * Math.PI * 2;
      this.am.play('ambient.cave', { pos: { x: p.x + Math.cos(a) * 6, y: p.y + 1, z: p.z + Math.sin(a) * 6 } });
    }
  }
}

export function attachAudio(d: AudioDeps): GameAudio { return new GameAudio(d); }
